import React, { useMemo, useState } from 'react';

import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import { FlagSetting, ReservationFlagDTO, ReservationFlagTypeEnum } from '@api/ringil-api.v2';
import { FlagIcon, sortFlags } from '@components/ringil3/Features/Reservation/flagIcon';
import ReservationFlagsField from '@components/RingilComponents/form/ReservationFlagsField';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';
import { findExceededUnitLimits, flagUnitsText, UnitCountableOrder } from '@components/RingilComponents/form/flagUnitsLimit';

interface ComponentProps {
  flags: ReservationFlagDTO[];
  flagsConfig?: Partial<Record<ReservationFlagTypeEnum, FlagSetting>>;
  onChange?: (flags: ReservationFlagDTO[]) => void;
  /** Orders booked on the reservation; caps the units of cross dock. Omit where they are unknown. */
  orders?: UnitCountableOrder[];
  readOnly?: boolean;
  dataTestId?: string;
  secured?: SecuredConfig;
}

const ReservationFlagsTag: React.FC<ComponentProps> = ({ flags, flagsConfig, onChange, orders, readOnly, dataTestId, secured }) => {
  const { tr } = useTranslationLgs();

  const sortedFlags = useMemo(() => sortFlags(flags ?? []), [flags]);
  const [editingFlags, setEditingFlags] = useState<ReservationFlagDTO[] | null>(null);
  const currentFlags = editingFlags ?? sortedFlags;

  const hasVisibleFlag = useMemo(
    () => Object.values(flagsConfig ?? {}).some(cfg => cfg?.visible),
    [flagsConfig],
  );

  // The flags endpoint cannot touch the orders that would make an over-cap value legal, so saving is
  // blocked outright; the cap message sits under the offending units field in the pane.
  // Kept above the early return below: a hook after it would not run on a render that bails out.
  const hasExceededUnitLimit = useMemo(() => findExceededUnitLimits(currentFlags, orders).length > 0, [currentFlags, orders]);

  if (!hasVisibleFlag) return null;

  const editable = !readOnly && !!onChange && !!flagsConfig;

  const value =
    sortedFlags.length > 0 ? (
      <>
        {sortedFlags.map((f, i) => (
          <React.Fragment key={f.reservationFlagType}>
            {i > 0 && <span className="text-content-tertiary mx-2">|</span>}
            <span className="inline-flex items-center gap-x-1 align-middle">
              <FlagIcon type={f.reservationFlagType} className="w-4 shrink-0" />
              {tr(`ReservationFlag.${f.reservationFlagType}`, f.reservationFlagType)}
              {flagUnitsText(f) != null && tr(`CalendarEventCardDetail.flagUnits`, ` ({{units}})`, { units: flagUnitsText(f) })}
            </span>
          </React.Fragment>
        ))}
      </>
    ) : undefined;

  const pane = editable ? (
    <ReservationFlagsField
      value={currentFlags}
      onChange={setEditingFlags}
      flagsConfig={flagsConfig!}
      orders={orders}
      dataTestIdPrefix={dataTestId}
    />
  ) : undefined;

  return (
    <ButtonTag
      label={tr(`ReservationFlagsTag.title`, `Flags`)}
      value={value}
      actionPane={pane}
      actionIcon={editable ? <SvgTagEdit /> : undefined}
      onSave={
        editable
          ? onSuccess => {
              if (hasExceededUnitLimit) return;
              onChange!(currentFlags);
              setEditingFlags(null);
              onSuccess();
            }
          : undefined
      }
      saveDisabled={hasExceededUnitLimit}
      onClose={() => setEditingFlags(null)}
      readonly={!editable}
      hideButtons={!editable}
      dataTestId={dataTestId}
      secured={secured}
    />
  );
};

export default ReservationFlagsTag;